import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { 
  MessageSquare, Image, Video, Sparkles, ArrowRight, Zap, Shield, Globe,
  Mic, Code, Brain, Cpu, Search, Calculator, TrendingUp, Languages,
  CheckCircle2, Star, ChevronRight, Play, Building2, Sofa, Box, TreePine,
  Layers, PenTool, Palette, Camera, Rocket, X, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import AnimatedLogo from '../components/common/AnimatedLogo';
import LanguageSwitcher from '../components/common/LanguageSwitcher';

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const { handleGoogleLogin, loginAsGuest, user } = useAuth();
  const navigate = useNavigate();
  const [activeDemo, setActiveDemo] = useState(0);
  const [showLoginOptions, setShowLoginOptions] = useState(false);
  const [showPrototypeModal, setShowPrototypeModal] = useState(true);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const isRTL = i18n.language === 'ar';

  // Custom Google login using authorization code flow
  // Using 'redirect' mode to avoid COOP issues in Firefox with popups
  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    ux_mode: 'popup', // Try popup first, will fallback if blocked
    onSuccess: async (tokenResponse) => {
      try {
        setIsGoogleLoading(true);
        console.log('Google login success, getting user info...');
        
        // Get user info from Google
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
        });
        
        if (!userInfoResponse.ok) {
          throw new Error(`Failed to get user info: ${userInfoResponse.status}`);
        }
        
        const userInfo = await userInfoResponse.json();
        console.log('Got user info:', userInfo.email);
        
        // Send to our backend
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        console.log('Sending to backend:', backendUrl);
        
        const response = await fetch(`${backendUrl}/api/auth/google/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            access_token: tokenResponse.access_token,
            email: userInfo.email,
            name: userInfo.name,
            picture: userInfo.picture,
            google_id: userInfo.sub
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Backend error: ${response.status} - ${errorText}`);
        }
        
        const data = await response.json();
        console.log('Login successful, redirecting...');
        localStorage.setItem('user', JSON.stringify(data));
        window.location.href = '/dashboard';
      } catch (error) {
        console.error('Google login error:', error);
        alert(`Login failed: ${error.message}. Please try again.`);
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google Login Failed:', error);
      setIsGoogleLoading(false);
      // If popup fails, suggest allowing popups
      alert('Google login failed. Please make sure popups are allowed in your browser settings and try again.');
    },
    onNonOAuthError: (error) => {
      console.error('Non-OAuth error:', error);
      setIsGoogleLoading(false);
      // This can happen with COOP issues - suggest using a different browser or allowing popups
      if (error.type === 'popup_closed' || error.type === 'popup_failed_to_open') {
        alert('Popup was blocked. Please allow popups for this site in your browser settings, or try using Chrome.');
      }
    }
  });

  // Google icon SVG component
  const GoogleIcon = () => (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  const demos = [
    { question: "Modern villa with glass walls", answer: "Generating photorealistic exterior render with V-Ray quality..." },
    { question: "Minimalist living room design", answer: "Creating Scandinavian interior with natural lighting..." },
    { question: "Japanese zen garden concept", answer: "Rendering landscape with stone pathways and koi pond..." },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveDemo((prev) => (prev + 1) % demos.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Set RTL direction
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  const features = [
    {
      icon: Building2,
      title: 'Exterior Architecture',
      description: 'Generate stunning exterior renders in Modern, Parametric, Brutalist, Classical, and more architectural styles.',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Sofa,
      title: 'Interior Design',
      description: 'Create beautiful interior visualizations with Minimalist, Scandinavian, Industrial, and Luxury styles.',
      gradient: 'from-amber-500 to-orange-500'
    },
    {
      icon: Box,
      title: '3D Rendering',
      description: 'Professional renders in V-Ray, Corona, Lumion, Enscape, and Octane quality with custom lighting.',
      gradient: 'from-emerald-500 to-teal-500'
    },
    {
      icon: TreePine,
      title: 'Landscape Design',
      description: 'Visualize gardens, plazas, rooftop terraces with Japanese, Mediterranean, and modern styles.',
      gradient: 'from-green-500 to-lime-500'
    },
    {
      icon: Video,
      title: 'Video Generation',
      description: 'Create walkthrough videos and animated presentations of your architectural concepts.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: MessageSquare,
      title: 'AI Design Assistant',
      description: 'Chat with AI about design concepts, get suggestions, and refine your creative vision.',
      gradient: 'from-rose-500 to-red-500'
    },
  ];

  const capabilities = [
    { icon: Building2, text: 'Exterior Renders' },
    { icon: Sofa, text: 'Interior Design' },
    { icon: Box, text: '3D Visualization' },
    { icon: TreePine, text: 'Landscape' },
    { icon: Camera, text: 'Product Shots' },
    { icon: Video, text: 'Video Gen' },
  ];

  const stats = [
    { value: '5', label: 'Design Studios' },
    { value: '50+', label: 'Render Styles' },
    { value: '8K', label: 'Quality Output' },
    { value: '24/7', label: 'Available' }
  ];

  return (
    <div className={`min-h-screen bg-zinc-950 text-white overflow-x-hidden ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Prototype Announcement Modal */}
      <Dialog open={showPrototypeModal} onOpenChange={setShowPrototypeModal}>
        <DialogContent className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 border-zinc-700 max-w-md text-center p-8">
          <button 
            onClick={() => setShowPrototypeModal(false)}
            className="absolute top-4 right-4 text-zinc-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center animate-pulse">
              <Rocket className="h-10 w-10 text-white" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-3">
            Welcome to JAF AI
          </h2>
          
          <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 mb-4">
            PROTOTYPE VERSION
          </Badge>
          
          <p className="text-zinc-300 mb-6 leading-relaxed">
            You're exploring our <span className="text-orange-400 font-semibold">prototype</span> version. 
            We're working hard to bring you the full-powered experience!
          </p>
          
          <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700 mb-6">
            <div className="flex items-center justify-center gap-2 text-emerald-400 mb-2">
              <Calendar className="h-5 w-5" />
              <span className="font-semibold">Full Version Coming</span>
            </div>
            <p className="text-2xl font-bold text-white">June 2026</p>
          </div>
          
          <Button 
            onClick={() => setShowPrototypeModal(false)}
            className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 text-white font-medium h-12"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Explore Prototype
          </Button>
          
          <p className="text-xs text-zinc-500 mt-4">
            Thank you for your patience and support!
          </p>
        </DialogContent>
      </Dialog>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <AnimatedLogo size="md" showText={true} />
            <div className="flex items-center gap-4">
              <LanguageSwitcher variant="ghost" />
              {user ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400"
                >
                  {t('nav.goToDashboard')}
                  <ArrowRight className={`h-4 w-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              ) : (
                <div className="relative">
                  <Button 
                    onClick={() => setShowLoginOptions(!showLoginOptions)}
                    className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 shadow-lg shadow-emerald-500/25"
                  >
                    {t('nav.getStarted')}
                  </Button>
                  
                  {/* Login Options Dropdown */}
                  {showLoginOptions && (
                    <div className="absolute top-full right-0 mt-2 w-64 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl p-4 z-50">
                      <Button 
                        onClick={() => googleLogin()}
                        disabled={isGoogleLoading}
                        className="w-full mb-3 bg-white hover:bg-zinc-100 text-zinc-900 font-medium"
                      >
                        <GoogleIcon />
                        <span className="ml-2">{isGoogleLoading ? 'Signing in...' : t('nav.signInWithGoogle')}</span>
                      </Button>
                      
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex-1 h-px bg-zinc-700"></div>
                        <span className="text-xs text-zinc-500">{t('landing.or')}</span>
                        <div className="flex-1 h-px bg-zinc-700"></div>
                      </div>
                      
                      <Button 
                        onClick={loginAsGuest}
                        variant="outline"
                        className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      >
                        {t('nav.continueAsGuest')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Background - Sketch to Render Image */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ 
              backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/72306723-d17d-4aa3-9129-6763d0fa2fb8/images/8e1a30e85804fd4954fd95901dae5b92028e7e24f111a91cc3f5537b2e846d0b.png')`,
              filter: 'blur(2px)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-950/90 to-zinc-950" />
          <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] bg-blue-500/20 rounded-full blur-[80px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-cyan-500/20 rounded-full blur-[80px]" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800/50 border border-zinc-700/50 mb-8 backdrop-blur-sm">
              <PenTool className="h-4 w-4 text-cyan-400" />
              <span className="text-sm text-zinc-300">From Sketch to Stunning Render in Seconds</span>
              <ChevronRight className={`h-4 w-4 text-zinc-500 ${isRTL ? 'rotate-180' : ''}`} />
            </div>
            
            {/* Main Heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
              <span className="bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">
                Transform Your
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                Architectural Vision
              </span>
            </h1>
            
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-8">
              Upload 3D models or sketches and transform them into photorealistic renders. V-Ray, Corona, Lumion quality renders for architects, interior designers, and visualization artists.
            </p>

            {/* Capabilities */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
              {capabilities.map((cap, i) => (
                <Badge key={i} variant="outline" className="px-3 py-1.5 bg-zinc-900/50 border-zinc-700 text-zinc-300">
                  <cap.icon className={`h-3.5 w-3.5 ${isRTL ? 'ml-1.5' : 'mr-1.5'} text-blue-400`} />
                  {cap.text}
                </Badge>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                onClick={() => googleLogin()}
                disabled={isGoogleLoading}
                className="w-full sm:w-auto bg-white hover:bg-zinc-100 text-zinc-900 text-lg px-8 py-6 shadow-xl group"
              >
                <GoogleIcon />
                <span className="ml-3">{isGoogleLoading ? 'Signing in...' : t('nav.signInWithGoogle')}</span>
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={loginAsGuest}
                className="w-full sm:w-auto border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white text-lg px-8 py-6 group"
              >
                {t('nav.continueAsGuest')}
                <ArrowRight className={`h-5 w-5 ${isRTL ? 'mr-2 rotate-180 group-hover:-translate-x-1' : 'ml-2 group-hover:translate-x-1'} transition-transform`} />
              </Button>
            </div>
          </div>

          {/* Sketch to Render Showcase */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="bg-zinc-900/80 backdrop-blur-xl rounded-2xl border border-zinc-800 p-2 shadow-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-800">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-xs text-zinc-500`}>3D Model → AI Render</span>
              </div>
              
              {/* Sketch to Render Image */}
              <div className="relative">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/72306723-d17d-4aa3-9129-6763d0fa2fb8/images/8e1a30e85804fd4954fd95901dae5b92028e7e24f111a91cc3f5537b2e846d0b.png"
                  alt="Sketch to Photorealistic Render"
                  className="w-full h-auto rounded-b-xl"
                />
                
                {/* Labels */}
                <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-zinc-900/90 backdrop-blur-sm rounded-lg border border-zinc-700">
                  <div className="flex items-center gap-2">
                    <PenTool className="h-4 w-4 text-zinc-400" />
                    <span className="text-sm text-zinc-300">Sketch / 3D Model</span>
                  </div>
                </div>
                <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-gradient-to-r from-cyan-600/90 to-blue-600/90 backdrop-blur-sm rounded-lg">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-white" />
                    <span className="text-sm text-white font-medium">AI Rendered</span>
                  </div>
                </div>
                
                {/* Center Arrow */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                    <ArrowRight className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Quick Demo Text */}
            <div className="mt-6 text-center">
              <p className="text-zinc-400 text-sm">
                Upload your <span className="text-cyan-400 font-medium">FBX, OBJ, or GLTF</span> file and transform it into a <span className="text-cyan-400 font-medium">photorealistic render</span> in seconds
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 max-w-4xl mx-auto">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">{stat.value}</div>
                <div className="text-sm text-zinc-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase Gallery Section */}
      <section className="py-16 px-4 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-500/20 text-purple-400 border-purple-500/30">
              AI Generated
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Stunning Renders in Seconds</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              See what JAF AI can create. Every image generated by our AI in under 30 seconds.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="group relative rounded-2xl overflow-hidden aspect-[4/3] border border-zinc-800 hover:border-blue-500/50 transition-all">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/70759d89-d880-4038-97fc-d46d1d05c914/images/fe6942961d04b4bc797347c9fdc881f0812586797b7f1e686c9ed2801522b9b0.png" 
                alt="Modern Villa Exterior" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-4 left-4 right-4">
                  <Badge className="bg-blue-500/90 text-white mb-2">Exterior</Badge>
                  <p className="text-white text-sm font-medium">Modern Villa at Golden Hour</p>
                  <p className="text-zinc-400 text-xs">V-Ray Quality • 8K Resolution</p>
                </div>
              </div>
            </div>

            <div className="group relative rounded-2xl overflow-hidden aspect-[4/3] border border-zinc-800 hover:border-amber-500/50 transition-all">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/70759d89-d880-4038-97fc-d46d1d05c914/images/72635e60cf544c4d16bc49a2617743c4342f9b06f488c3e236ee8f6cc7b131bc.png" 
                alt="Scandinavian Living Room" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-4 left-4 right-4">
                  <Badge className="bg-amber-500/90 text-white mb-2">Interior</Badge>
                  <p className="text-white text-sm font-medium">Scandinavian Living Room</p>
                  <p className="text-zinc-400 text-xs">Corona Render • Natural Light</p>
                </div>
              </div>
            </div>

            <div className="group relative rounded-2xl overflow-hidden aspect-[4/3] border border-zinc-800 hover:border-green-500/50 transition-all">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/70759d89-d880-4038-97fc-d46d1d05c914/images/7a4e0f128424c179ebd742a48a945438e2080051bbed281e25494158878219f9.png" 
                alt="Japanese Zen Garden" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-4 left-4 right-4">
                  <Badge className="bg-green-500/90 text-white mb-2">Landscape</Badge>
                  <p className="text-white text-sm font-medium">Japanese Zen Garden</p>
                  <p className="text-zinc-400 text-xs">Lumion Style • Koi Pond</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button 
              variant="outline" 
              onClick={loginAsGuest}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Try It Free - Generate Your First Render
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-900/50 to-zinc-950" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-500/20 text-blue-400 border-blue-500/30">
              Design Tools
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Everything You Need to Visualize</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Professional-grade AI tools for architects, interior designers, landscape architects, and creative professionals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div
                key={i}
                className="group p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Render Engines Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
              Professional Quality
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Industry-Standard Render Quality</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Generate visualizations that match the quality of leading render engines used by top architecture firms.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'V-Ray Style', desc: 'Photorealistic rendering with accurate lighting', speed: 'Ultra Realistic', color: 'blue', icon: '🎯' },
              { name: 'Corona Style', desc: 'Soft lighting and realistic materials', speed: 'Architectural', color: 'amber', icon: '☀️' },
              { name: 'Lumion Style', desc: 'Atmospheric and cinematic quality', speed: 'Cinematic', color: 'emerald', icon: '🌅' },
              { name: 'Enscape Style', desc: 'Real-time render look', speed: 'Real-time', color: 'purple', icon: '🏠' },
            ].map((model, i) => (
              <div key={i} className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-all text-center">
                <div className={`w-16 h-16 rounded-2xl bg-${model.color}-500/20 flex items-center justify-center mx-auto mb-4 text-3xl`}>
                  {model.icon}
                </div>
                <h3 className="text-lg font-semibold text-white">{model.name}</h3>
                <p className="text-sm text-zinc-500">{model.desc}</p>
                <Badge variant="outline" className="mt-3">{model.speed}</Badge>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-12 rounded-3xl bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-emerald-500/10 border border-zinc-800">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Transform Your Design Workflow?</h2>
            <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
              Join thousands of architects and designers creating stunning visualizations with AI. No rendering farm needed.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                onClick={() => googleLogin()}
                disabled={isGoogleLoading}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 text-lg px-10 py-6 shadow-xl shadow-blue-500/25"
              >
                <GoogleIcon />
                <span className="ml-2">Start with Google</span>
              </Button>
              <Button 
                size="lg"
                onClick={loginAsGuest}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 px-8 py-6"
              >
                Try as Guest
                <ArrowRight className={`h-5 w-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </div>
            <p className="text-xs text-zinc-500 mt-4">Free to start • No credit card required</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <AnimatedLogo size="sm" showText={true} />
          <p className="text-sm text-zinc-500">© 2025 JAF AI. Built for architects & designers.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
